import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

// Auth context wraps the app: exposes {user, loading, login, completeTwoFA, logout}.
// On mount we try /api/auth/me to see if cookies are still valid; that drives
// whether we render the protected app or the login screen.

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const IDLE_MS = 30 * 60 * 1000;  // 30 min — matches ACCESS_TOKEN_MINUTES on the server

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimerRef = useRef(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe().finally(() => setLoading(false));
  }, [refreshMe]);

  // 401 from any protected call → drop the user state so the route guard
  // shows the login screen.
  useEffect(() => {
    const onUnauthed = () => setUser(null);
    window.addEventListener("auth:401", onUnauthed);
    return () => window.removeEventListener("auth:401", onUnauthed);
  }, []);

  // Idle timer — silently logs the user out after 10 min of no
  // mousemove/keydown/click. Server-side, the access_token also expires after
  // 10 min so even if the timer is bypassed, sessions can't outlive that
  // window without an explicit /auth/refresh roundtrip.
  useEffect(() => {
    if (!user) return undefined;
    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(async () => {
        try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
        setUser(null);
      }, IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user]);

  const login = useCallback(async (email, password, role) => {
    const { data } = await api.post("/auth/login", { email, password, role });
    // For partner/merchant the server may sign them in directly (stage="signed_in")
    if (data.stage === "signed_in" && data.user) {
      setUser(data.user);
    }
    return data;
  }, []);

  const demoLogin = useCallback(async (role) => {
    // role can be "admin" (default — synthetic demo user) | "partner" | "merchant"
    // For partner/merchant, the backend signs in the seeded demo user directly
    // — no client-side credential. demo:true claim routes all queries to the
    // sandbox DB.
    const payload = (role === "partner" || role === "merchant") ? { role } : {};
    const { data } = await api.post("/auth/demo-login", payload);
    if (data.user) setUser(data.user);
    return data.user;
  }, []);

  const completeTwoFA = useCallback(async (twofa_token, code, isSetup) => {
    const url = isSetup ? "/auth/2fa/setup-confirm" : "/auth/2fa/verify";
    const { data } = await api.post(url, { twofa_token, code });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_e) { /* ignore */ }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, demoLogin, completeTwoFA, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}
